// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Orchestrates all state for analysis node admin views.
 * Composes domain-specific hooks (model, dataset) into a unified interface.
 */
import { useCallback } from 'react'
import { useAgentDatasets, useDatasets } from '@/subsystems/pipelines/hooks'
import { useAnalysisModelState } from './useAnalysisModelState'
import { useDatasetState } from './useDatasetState'
import type { AnalysisPromptConfig } from '../types'

/** Findings KBs have batch controls; report KBs process one section at a time */
export type KnowledgeBaseType = 'findings' | 'report' | 'unknown'

/** Typed config updater - replaces generic onChange(key, value) pattern */
export type ConfigUpdater = (updates: Partial<AnalysisPromptConfig>) => void

interface UseAnalysisPromptStateParams {
  orgId: string
  workspaceId: string
  /** Owning agent when configuring a node in an agent's pipeline. */
  agentId?: string
  nodeInstanceId?: string
  config?: Record<string, unknown>
  onChange?: (key: string, value: unknown) => void
}

export const useAnalysisPromptState = ({
  orgId,
  workspaceId,
  agentId,
  config,
  onChange,
}: UseAnalysisPromptStateParams) => {
  // Cast config to typed shape; parent provides generic WidgetConfig
  const analysisConfig = (config ?? {}) as AnalysisPromptConfig

  // Typed adapter: converts parent's generic onChange to typed partial updates
  const updateConfig: ConfigUpdater = useCallback(
    (updates: Partial<AnalysisPromptConfig>) => {
      Object.entries(updates).forEach(([key, value]) => {
        onChange?.(key, value)
      })
    },
    [onChange]
  )

  // The owning agent's datasets; outside an agent pipeline, fall back to every
  // agent's datasets in the workspace (the workspace hook is disabled otherwise).
  const workspaceDatasets = useDatasets(agentId ? undefined : orgId, workspaceId)
  const agentDatasets = useAgentDatasets(orgId, workspaceId, agentId)
  const { datasets } = agentId ? agentDatasets : workspaceDatasets

  const modelState = useAnalysisModelState({ analysisConfig, updateConfig })

  const datasetState = useDatasetState({
    analysisConfig,
    updateConfig,
    datasets,
  })

  // Datasource state
  const datasourceType = analysisConfig.datasourceType ?? null
  const datasourceConfig = analysisConfig.datasourceConfig ?? null

  const handleDatasourceTypeChange = useCallback(
    (type: AnalysisPromptConfig['datasourceType']) => {
      updateConfig({
        datasourceType: type,
        datasourceConfig: type === null ? null : datasourceConfig,
      })
    },
    [datasourceConfig, updateConfig]
  )

  const handleDatasourceConfigChange = useCallback(
    (key: string, value: unknown) => {
      updateConfig({
        datasourceConfig: {
          ...(datasourceConfig ?? {}),
          [key]: value,
        },
      })
    },
    [datasourceConfig, updateConfig]
  )

  const handlers = {
    handleModelChange: modelState.handlers.handleModelChange,
    handleDatasetKeyChange: datasetState.handlers.handleDatasetKeyChange,
    handleDatasourceTypeChange,
    handleDatasourceConfigChange,
    handleLongContextFallbackEnabledChange: modelState.handlers.handleLongContextFallbackEnabledChange,
    handleLongContextFallbackModelChange: modelState.handlers.handleLongContextFallbackModelChange,
  }

  return {
    settingsLoading: false,
    modelVariant: modelState.modelVariant,
    datasetKey: datasetState.datasetKey,
    datasetList: datasetState.datasetList,
    supportsCaching: modelState.supportsCaching,
    longContextFallbackEnabled: modelState.longContextFallbackEnabled,
    longContextFallbackModel: modelState.longContextFallbackModel,
    datasourceType,
    datasourceConfig,
    handlers,
  }
}
